/**
 * Image optimizer for imgproxy — the self-hosted image transformation proxy.
 *
 * imgproxy sits between the client and R2, resizing/reformatting images
 * on the fly. URL format:
 *
 *   {imgproxyUrl}/{processing}/plain/{s3Key}
 *
 * Processing options (slash-separated):
 *   rs:fit:W:H   — resize fit-inside WxH, keep aspect ratio
 *   rs:fill:W:H  — resize to cover WxH, crop excess
 *   q:N          — quality 1-100
 *   @webp        — force WebP output (appended to key)
 *   @jpg         — force JPG output (appended to key)
 *
 * Without processing: {imgproxyUrl}/plain/{s3Key} (passthrough)
 *
 * Docs: https://docs.imgproxy.net/usage/processing
 */

// Images are limited to 25 MP. Keep the safeguard.
const IMAGE_SIZE_LIMIT = 25e6;

const getMaxWidth = (width: number, aspectRatio: number) => {
    const maxWidth = Math.sqrt(IMAGE_SIZE_LIMIT) * Math.sqrt(aspectRatio);
    return Math.floor(Math.min(maxWidth, width));
};

/**
 * Check whether a given src URL can be optimised via our imgproxy endpoint.
 * Only URLs that are already imgproxy URLs are further optimisable.
 */
export function canOptimize(src: string, imgproxyUrl: string): boolean {
    if (!imgproxyUrl) return false;
    return src.startsWith(imgproxyUrl);
}

/**
 * Cloudinary CDN URL detection — both the public `*.mo.cloudinary.net/p/{key}`
 * format and PPI's `cdn.visualpedia.igg.cloud/{key}` alias (same Cloudinary
 * backend, custom hostname). Both accept the same `?tx=...` transformation
 * syntax.
 *
 * Why this matters: scrollys imported from PPI (or earlier scrolly.com)
 * carry legacy Cloudinary URLs in their JSON. Without rewriting these URLs
 * with width-limit + format transforms, the THREE.ImageLoader requests the
 * original asset (often 4096×4096 PNG, ~64 MB GPU texture once decoded).
 * 50+ such layers blow the iOS Safari WebGL texture budget (~256–384 MB)
 * and the tab crashes. scrlly.com (PPI's legacy renderer) sends
 * `?tx=f_auto,c_limit,c_scale,w_1200,dpr_1.0`; we match the pattern.
 */
function isCloudinaryUrl(src: string): boolean {
    return /:\/\/[^/]*\.cloudinary\.net\//.test(src) ||
        src.includes("://cdn.visualpedia.igg.cloud/");
}

/**
 * Parse the target width (in device pixels) from an imgproxy `processing`
 * string. Accepts both `rs:fit:W` and `rs:fit:W:H` forms (with optional
 * `/q:N` quality suffix). Returns `undefined` if no width could be parsed.
 */
function parseTargetWidthFromProcessing(processing: string | undefined): number | undefined {
    if (!processing) return undefined;
    const m = processing.match(/rs:fit:(\d+)/);
    if (!m) return undefined;
    const width = parseInt(m[1], 10);
    return Number.isFinite(width) && width > 0 ? width : undefined;
}

/**
 * Append Cloudinary transformation params to an existing Cloudinary URL.
 * Idempotent — if the URL already has a `tx=` param we leave it alone so
 * that callers can override defaults.
 *
 * Transform-set rationale:
 *   - `f_auto`  → Cloudinary picks WebP/AVIF for capable browsers
 *   - `c_limit` → never upscale beyond the original (preserves quality
 *                 for already-small assets)
 *   - `w_{W}`   → target width in device pixels
 *   - `dpr_1.0` → the caller (RendererWebGL) already folded DPR into the
 *                 width calculation, so we lock Cloudinary's DPR multiplier
 *                 to 1.0 to avoid double-applying it
 */
function appendCloudinaryTransform(src: string, width: number): string {
    if (/[?&]tx=/.test(src)) return src;
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}tx=f_auto,c_limit,w_${width},dpr_1.0`;
}

export type ImageOptimizer = (src: string, params: Map<string, string>) => string;

/**
 * Create an optimizer that rewrites image URLs with transformation params.
 *
 * Two code paths:
 *   1. imgproxy URLs (caller-configured endpoint) — rebuilds the URL with
 *      the imgproxy `{ops}/plain/{key}` syntax from the `processing` param.
 *   2. Cloudinary URLs (`*.mo.cloudinary.net`, `cdn.visualpedia.igg.cloud`)
 *      — appends `?tx=f_auto,c_limit,w_{W},dpr_1.0`, where W is parsed out
 *      of the same `processing` param (so callers don't need to know which
 *      CDN they're hitting).
 *
 * URLs that match neither pattern are returned unchanged.
 */
export function createImageOptimizer(imgproxyUrl: string): ImageOptimizer {
    const endpoint = (imgproxyUrl || "").replace(/\/$/, "");
    return (src, params) => {
        if (!src) return src;

        if (endpoint && canOptimize(src, endpoint)) {
            // Extract the S3 key from the URL. imgproxy URLs look like:
            //   {endpoint}/plain/{key}           (no processing)
            //   {endpoint}/{ops}/plain/{key}     (with processing)
            const plainIdx = src.indexOf("/plain/");
            if (plainIdx === -1) return src;
            const s3Key = src.substring(plainIdx + "/plain/".length);

            // Build the processing string from params.
            // Convention: caller sets `processing` key with the full imgproxy ops string
            //   e.g. "rs:fit:400/q:80"
            const processing = params.get("processing");
            if (processing) {
                return `${endpoint}/${processing}/plain/${s3Key}`;
            }

            return src;
        }

        if (isCloudinaryUrl(src)) {
            const targetWidth = parseTargetWidthFromProcessing(params.get("processing"));
            if (targetWidth === undefined) return src;
            return appendCloudinaryTransform(src, targetWidth);
        }

        return src;
    };
}

/**
 * Build an optimised image URL via imgproxy.
 *
 * @param src the original image source (must be an imgproxy URL)
 * @param requestedWidth the requested width in CSS pixels
 * @param aspectRatio width/height of the image
 * @param imgproxyUrl imgproxy endpoint (e.g. https://api.scrolly.com/img)
 * @param quality optional quality override (1–100, default 80)
 * @param imageFormat optional format override ("webp", "jpg", "png")
 */
export function imageLoader(
    src: string,
    requestedWidth: number,
    aspectRatio: number,
    imgproxyUrl: string,
    quality?: number | string,
    imageFormat?: string
): string {
    const optimizer = createImageOptimizer(imgproxyUrl);
    const maxWidth = getMaxWidth(requestedWidth, aspectRatio);
    const outputWidth = Math.min(requestedWidth, maxWidth);
    const h = Math.round(outputWidth / aspectRatio);

    const ops = [`rs:fit:${Math.floor(outputWidth)}:${h}`];
    if (quality && quality !== "auto") ops.push(`q:${quality}`);

    const params = new Map<string, string>();
    params.set("processing", ops.join("/"));

    let result = optimizer(src, params);

    // Append format suffix if requested (e.g. @webp)
    if (imageFormat && imageFormat !== "auto" && !result.endsWith(`@${imageFormat}`)) {
        result += `@${imageFormat}`;
    }

    return result;
}

export default imageLoader;
